/** Severity weights for heatmap intensity by incident type */
export const HEATMAP_SEVERITY_WEIGHTS = {
  'car-collision': 1.0,
  'multiple-vehicle-incident': 1.3,
  'car-pedestrian': 1.5,
  'car-bicycle': 1.2,
  'car-motorcycle': 1.3,
  'car-animal': 0.8,
  'car-property': 0.7,
  'car-rollover': 1.4,
  'car-fire': 1.6,
  'motorcycle-collision': 1.3,
  'motorcycle-car': 1.3,
  'truck-collision': 1.4,
  'bicycle-collision': 1.1,
  'pedestrian-collision': 1.5,
  'near-miss': 0.9,
  'near-miss-car': 0.9,
  'near-miss-motorcycle': 0.9,
  'near-miss-truck': 0.9,
  'near-miss-bicycle': 0.9,
  'near-miss-pedestrian': 0.9,
  'dangerous-driving': 1.2,
  'dangerous-driving-car': 1.2,
  'dangerous-driving-motorcycle': 1.2,
  'dangerous-driving-truck': 1.2,
  'dangerous-driving-bicycle': 1.2,
  'bicycle-incident': 1.3,
  'pedestrian-incident': 1.4,
  'traffic-light-malfunction': 0.7,
  'traffic-light-damage': 0.7,
  'road-hazard': 0.8,
  'debris-on-road': 0.7,
  'pothole-or-surface-damage': 0.7,
  'signage-issue': 0.7,
  'roadwork-issue': 0.7,
  'traffic-congestion-or-gridlock': 0.7,
  'other-traffic-hazard': 0.7,
  other: 1.0,
};

/**
 * Google Maps HeatmapLayer default: low density green → high density red.
 * @see https://developers.google.com/maps/documentation/javascript/heatmaplayer
 */
export const GOOGLE_HEATMAP_GRADIENT_CLASSIC = [
  'rgba(102, 255, 0, 0)',
  'rgba(102, 255, 0, 1)',
  'rgba(147, 255, 0, 1)',
  'rgba(193, 255, 0, 1)',
  'rgba(238, 255, 0, 1)',
  'rgba(244, 227, 0, 1)',
  'rgba(249, 198, 0, 1)',
  'rgba(255, 170, 0, 1)',
  'rgba(255, 113, 0, 1)',
  'rgba(255, 57, 0, 1)',
  'rgba(255, 0, 0, 1)',
];

/** Alternate Google-style palette: transparent cyan → blue → red (cool tones). */
export const GOOGLE_HEATMAP_GRADIENT_COOL = [
  'rgba(0, 255, 255, 0)',
  'rgba(0, 255, 255, 1)',
  'rgba(0, 191, 255, 1)',
  'rgba(0, 127, 255, 1)',
  'rgba(0, 63, 255, 1)',
  'rgba(0, 0, 255, 1)',
  'rgba(0, 0, 223, 1)',
  'rgba(0, 0, 191, 1)',
  'rgba(0, 0, 159, 1)',
  'rgba(0, 0, 127, 1)',
  'rgba(63, 0, 91, 1)',
  'rgba(127, 0, 63, 1)',
  'rgba(191, 0, 31, 1)',
  'rgba(255, 0, 0, 1)',
];

/** Default matches Google’s documented green (low) → red (high) scheme. */
export const DEFAULT_HEATMAP_GRADIENT = GOOGLE_HEATMAP_GRADIENT_CLASSIC;

export const HEATMAP_GRADIENT_PRESETS = {
  classic: {
    label: 'Google classic (green → red)',
    gradient: GOOGLE_HEATMAP_GRADIENT_CLASSIC,
  },
  cool: {
    label: 'Google cool (cyan → red)',
    gradient: GOOGLE_HEATMAP_GRADIENT_COOL,
  },
};

/**
 * Parse lat/lng from report coordinates (string or object).
 * @returns {{ lat: number, lng: number } | null}
 */
export function parseReportCoordinates(coordinates) {
  if (!coordinates) return null;

  if (typeof coordinates === 'object' && coordinates.lat != null && coordinates.lng != null) {
    const lat = parseFloat(coordinates.lat);
    const lng = parseFloat(coordinates.lng);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }
    return null;
  }

  if (typeof coordinates === 'string') {
    const match = coordinates.trim().match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (!match) return null;
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }
  }

  return null;
}

/**
 * @param {Array<{ coordinates?: unknown, incidentType?: string }>} reports
 * @returns {Array<{ lat: number, lng: number, weight: number }>}
 */
export function reportsToHeatmapPoints(reports, weights = HEATMAP_SEVERITY_WEIGHTS) {
  if (!Array.isArray(reports)) return [];

  return reports
    .map(report => {
      const coords = parseReportCoordinates(report.coordinates);
      if (!coords) return null;
      return {
        lat: coords.lat,
        lng: coords.lng,
        weight: weights[report.incidentType] ?? 1.0,
      };
    })
    .filter(Boolean);
}

/**
 * Approximate meters per pixel at a latitude/zoom (Web Mercator).
 */
export function metersToPixels(meters, lat, zoom) {
  const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  return meters / metersPerPixel;
}

/** Parse rgba/rgb string into components */
export function parseRgbaColor(color) {
  if (typeof color !== 'string') return null;
  const match = color
    .trim()
    .match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
  if (!match) return null;
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
    a: match[4] !== undefined ? parseFloat(match[4]) : 1,
  };
}

export function rgbaToString({ r, g, b, a }) {
  const alpha = Math.min(1, Math.max(0, a ?? 1));
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}

export function rgbToHex(r, g, b) {
  const toHex = n =>
    Math.min(255, Math.max(0, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function cloneDefaultGradient() {
  return [...DEFAULT_HEATMAP_GRADIENT];
}

export function normalizeHeatmapGradient(value) {
  if (Array.isArray(value) && value.length >= 3) {
    const parsed = value
      .map(color => parseRgbaColor(color))
      .filter(Boolean)
      .map(rgbaToString);
    if (parsed.length >= 3) return parsed;
  }

  if (typeof value === 'string') {
    try {
      return normalizeHeatmapGradient(JSON.parse(value));
    } catch {
      return cloneDefaultGradient();
    }
  }

  return cloneDefaultGradient();
}

/** Build CSS linear-gradient from heatmap stops */
export function gradientToCssBackground(gradient, direction = 'to right') {
  const colors = normalizeHeatmapGradient(gradient);
  const visible = colors.filter(color => {
    const parsed = parseRgbaColor(color);
    return parsed && parsed.a > 0;
  });
  const stops = (visible.length >= 2 ? visible : colors).join(', ');
  return `linear-gradient(${direction}, ${stops})`;
}

/** Split gradient into low/medium/high legend segments */
export function gradientLegendSegments(gradient) {
  const colors = normalizeHeatmapGradient(gradient).filter(color => {
    const parsed = parseRgbaColor(color);
    return parsed && parsed.a > 0;
  });

  if (colors.length < 3) {
    return {
      low: gradientToCssBackground(gradient),
      medium: gradientToCssBackground(gradient),
      high: gradientToCssBackground(gradient),
    };
  }

  const third = Math.max(1, Math.floor(colors.length / 3));
  const lowStart = colors[0];
  const lowEnd = colors[Math.min(third, colors.length - 1)];
  const midStart = colors[Math.min(third, colors.length - 2)];
  const midEnd = colors[Math.min(third * 2, colors.length - 1)];
  const highStart = colors[Math.min(third * 2, colors.length - 2)];
  const highEnd = colors[colors.length - 1];

  return {
    low: `linear-gradient(to right, ${lowStart}, ${lowEnd})`,
    medium: `linear-gradient(to right, ${midStart}, ${midEnd})`,
    high: `linear-gradient(to right, ${highStart}, ${highEnd})`,
  };
}
